import { normalizeVisualizationColumnCells } from './table-column.util';
import { TABLE_EMPTY_CELL_LABEL } from './table-grid.constants';
import { visualizationRecord } from './table-presentation-visualization.util';

/** Table tab column display modes mapped to Grid Pro `cells` options. */
export type TableColumnRenderMode =
	| 'auto'
	| 'text'
	| 'lineBreaks'
	| 'link'
	| 'image'
	| 'sparkline'
	| 'custom';

export const TABLE_COLUMN_RENDER_MODE_OPTIONS: ReadonlyArray<{
	value: Exclude<TableColumnRenderMode, 'custom'>;
	label: string;
}> = [
	{ value: 'auto', label: 'Default formatting' },
	{ value: 'text', label: 'Plain text' },
	{ value: 'lineBreaks', label: 'Text with line breaks' },
	{ value: 'link', label: 'Link' },
	{ value: 'image', label: 'Image' },
	{ value: 'sparkline', label: 'Sparkline' },
];

const PLAIN_TEXT_FORMAT = '{value}';

const LINK_CELL_FORMAT =
	'<a href="{value}" target="_blank" rel="noopener noreferrer">{value}</a>';

/** Sizing lives in the table card SCSS so the image shows uncropped at its own aspect ratio. */
const IMAGE_CELL_FORMAT =
	'<img src="{value}" alt="" loading="lazy" decoding="async" />';

/** Image template saved before JEP-10815 (32px cropped circle). Upgraded on render. */
const LEGACY_IMAGE_CELL_FORMAT =
	'<img src="{value}" alt="" loading="lazy" decoding="async" style="width:32px;height:32px;border-radius:50%;object-fit:cover;display:block" />';

/**
 * Jepto-only `cells` marker for the line breaks mode. Grid Pro cannot persist a
 * formatter function, so the table card swaps it for {@link lineBreaksCellFormatter}.
 */
const LINE_BREAKS_CELLS_KEY = 'lineBreaks';

/** Real newlines plus literal `\n` / `\r\n` escape sequences left in source text. */
const LINE_BREAK_PATTERN = /\r\n|\r|\n|\\r\\n|\\n/g;

function visualizationColumnCells(
	visualization: unknown,
	columnId: string
): Record<string, unknown> | undefined {
	const columns = visualizationRecord(visualization)['columns'];
	if (!Array.isArray(columns)) {
		return undefined;
	}
	const entry = columns.find(
		col =>
			typeof col === 'object' &&
			col !== null &&
			(col as Record<string, unknown>)['id'] === columnId
	);
	if (!entry || typeof entry !== 'object') {
		return undefined;
	}
	const cells = (entry as Record<string, unknown>)['cells'];
	if (!cells || typeof cells !== 'object' || Array.isArray(cells)) {
		return undefined;
	}
	return cells as Record<string, unknown>;
}

function isImageCellsFormat(format: string): boolean {
	return /<img\b/i.test(format);
}

function isLinkCellsFormat(format: string): boolean {
	const trimmed = format.trim();
	return (
		/<a\b/i.test(trimmed) &&
		/href\s*=\s*["'{]/i.test(trimmed) &&
		/\{value\}/.test(trimmed)
	);
}

/** Resolves the Table tab render mode from a persisted `visualization.columns[].cells` entry. */
export function renderModeFromVisualizationCells(
	cells: Record<string, unknown> | null | undefined
): TableColumnRenderMode {
	if (!cells) {
		return 'auto';
	}

	if (cells[LINE_BREAKS_CELLS_KEY] === true) {
		return 'lineBreaks';
	}

	const renderer = cells['renderer'];
	if (
		renderer &&
		typeof renderer === 'object' &&
		!Array.isArray(renderer) &&
		(renderer as Record<string, unknown>)['type'] === 'sparkline'
	) {
		return 'sparkline';
	}

	const format = cells['format'];
	if (typeof format !== 'string' || !format.trim()) {
		return 'auto';
	}

	if (format.trim() === PLAIN_TEXT_FORMAT) {
		return 'text';
	}
	if (isLinkCellsFormat(format)) {
		return 'link';
	}
	if (isImageCellsFormat(format)) {
		return 'image';
	}
	return 'custom';
}

export function renderModeFromVisualizationColumn(
	visualization: unknown,
	columnId: string
): TableColumnRenderMode {
	return renderModeFromVisualizationCells(
		visualizationColumnCells(visualization, columnId)
	);
}

/** Grid Pro `cells` fragment for a Table tab render mode (`auto` clears overrides). */
export function gridCellsForRenderMode(
	mode: Exclude<TableColumnRenderMode, 'custom' | 'auto'>
): Record<string, unknown> {
	switch (mode) {
		case 'text':
			return { format: PLAIN_TEXT_FORMAT };
		case 'lineBreaks':
			return { [LINE_BREAKS_CELLS_KEY]: true };
		case 'link':
			return { format: LINK_CELL_FORMAT };
		case 'image':
			return { format: IMAGE_CELL_FORMAT };
		case 'sparkline':
			return { renderer: { type: 'sparkline' } };
	}
}

function escapeCellHtml(value: string): string {
	return value
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;');
}

/** Grid Pro `cells.formatter` for the line breaks mode: escapes the value and turns newlines into `<br>`. */
export function lineBreaksCellFormatter(this: { value: unknown }): string {
	const value = this.value;
	if (value == null || value === '') {
		return TABLE_EMPTY_CELL_LABEL;
	}
	return escapeCellHtml(String(value)).replace(LINE_BREAK_PATTERN, '<br>');
}

/**
 * Resolves Jepto-only `cells` settings on a merged grid column into options Grid Pro
 * understands: upgrades the legacy cropped-circle image template and attaches the
 * line breaks formatter. The `lineBreaks` marker stays on `cells` so
 * `gridColumnsCellsFingerprint` can tell it apart from the default formatter.
 */
export function resolveGridColumnRenderCells(
	column: Record<string, unknown>
): Record<string, unknown> {
	const cells = column['cells'];
	if (!cells || typeof cells !== 'object' || Array.isArray(cells)) {
		return column;
	}
	const cellsRecord = cells as Record<string, unknown>;
	if (cellsRecord['format'] === LEGACY_IMAGE_CELL_FORMAT) {
		return {
			...column,
			cells: { ...cellsRecord, format: IMAGE_CELL_FORMAT },
		};
	}
	if (cellsRecord[LINE_BREAKS_CELLS_KEY] === true) {
		const {
			format: _format,
			renderer: _renderer,
			...cellsRest
		} = cellsRecord;
		return {
			...column,
			cells: { ...cellsRest, formatter: lineBreaksCellFormatter },
		};
	}
	return column;
}

function columnOverrideWithoutCells(
	column: Record<string, unknown>
): Record<string, unknown> | null {
	const { cells: _cells, ...rest } = column;
	const keys = Object.keys(rest).filter(key => key !== 'id');
	if (keys.length === 0) {
		return null;
	}
	return rest;
}

/** Applies a Table tab render mode to `visualization.columns` for one column id. */
export function mergeColumnRenderModeIntoVisualization(
	visualization: unknown,
	columnId: string,
	mode: TableColumnRenderMode
): Record<string, unknown> {
	if (mode === 'custom') {
		return visualizationRecord(visualization);
	}

	if (mode === 'auto') {
		const base = visualizationRecord(visualization);
		if (!Array.isArray(base['columns'])) {
			return base;
		}
		const columns = (base['columns'] as Array<Record<string, unknown>>).flatMap(
			entry => {
				if (entry['id'] !== columnId) {
					return [entry];
				}
				const withoutCells = columnOverrideWithoutCells(entry);
				return withoutCells ? [withoutCells] : [];
			}
		);
		return { ...base, columns };
	}

	const base = visualizationRecord(visualization);
	const existingColumns = Array.isArray(base['columns'])
		? ([...(base['columns'] as Array<Record<string, unknown>>)] as Array<
				Record<string, unknown>
			>)
		: [];
	const index = existingColumns.findIndex(entry => entry['id'] === columnId);
	const existing =
		index >= 0 ? { ...(existingColumns[index] ?? {}) } : { id: columnId };
	const mergedColumn = normalizeVisualizationColumnCells({
		...existing,
		id: columnId,
		cells: gridCellsForRenderMode(mode),
	});
	if (index >= 0) {
		existingColumns[index] = mergedColumn;
	} else {
		existingColumns.push(mergedColumn);
	}
	return { ...base, columns: existingColumns };
}

/**
 * Returns a copy of `visualization` with the `cells` key dropped from every
 * `columns[]` entry. Used by the table editor Data preview tab so Display as
 * (link/image/sparkline/text) renderers do not apply to raw data, while
 * column widths, order, and other visualization keys are preserved.
 *
 * Returns the same reference when there is nothing to strip (no `columns`
 * array, or no column carries a `cells` key) so downstream `===` checks are
 * unaffected. Never mutates the input.
 */
export function stripCellsFromVisualizationColumns(
	visualization: unknown
): Record<string, unknown> {
	const isPlainObject =
		visualization !== null &&
		typeof visualization === 'object' &&
		!Array.isArray(visualization);
	const base = visualizationRecord(visualization);
	const columns = base['columns'];
	if (!Array.isArray(columns)) {
		return isPlainObject
			? (visualization as Record<string, unknown>)
			: base;
	}
	let changed = false;
	const nextColumns = (columns as Array<Record<string, unknown>>).map(
		column => {
			if (
				!column ||
				typeof column !== 'object' ||
				Array.isArray(column)
			) {
				return column;
			}
			if (!Object.prototype.hasOwnProperty.call(column, 'cells')) {
				return column;
			}
			changed = true;
			const { cells: _cells, ...rest } = column;
			return rest;
		}
	);
	if (!changed) {
		return isPlainObject
			? (visualization as Record<string, unknown>)
			: base;
	}
	return { ...base, columns: nextColumns };
}
